import React from "react";
import { CircleUser } from "lucide-react";
import { JobItemEntityType } from "@/lib/supabase/supabaseTypes";
import { useUserManagementStore } from "@/dentlab/src/store/UserManagement";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/dentlab/src/components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/dentlab/src/components/ui/tooltip";
import { getProfileDisplayName } from "@/lib/utils/client-utils";

interface TechnicianHoverMenuProps {
	jobItem: JobItemEntityType;
}

const generateLabel = (profile: any) => {
	return (
		getProfileDisplayName(profile, "Unbekannt") ||
		profile.code ||
		"Unbekannt"
	);
};

export const TechnicianHoverMenu: React.FC<TechnicianHoverMenuProps> = ({
	jobItem,
}) => {
	const { activeUsers } = useUserManagementStore((state) => ({
		activeUsers: state.getActiveMembers(new Date()),
	}));

	const firstTechnicianPercentage =
		100 - (jobItem.techn_perc_2 ?? 0) - (jobItem.techn_perc_3 ?? 0);

	const technicians = [
		{
			profileId: jobItem.profile_id,
			percentage: firstTechnicianPercentage,
		},
		{
			profileId: jobItem.profile2_id,
			percentage: jobItem.techn_perc_2,
		},
		{
			profileId: jobItem.profile3_id,
			percentage: jobItem.techn_perc_3,
		},
	].filter((tech) => tech.profileId);

	if (technicians.length === 0) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<CircleUser className="w-4 h-4 text-gray-400 transition-colors" />
				</TooltipTrigger>
				<TooltipContent>
					<p className="text-xs">Keine Techniker zugewiesen</p>
				</TooltipContent>
			</Tooltip>
		);
	}

	return (
		<Popover>
			<PopoverTrigger asChild>
				<div className="cursor-pointer">
					<CircleUser className="w-4 h-4 hover:text-blue-600 transition-colors" />
				</div>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-2" side="right">
				<div className="space-y-1">
					<div className="text-xs font-semibold text-gray-600 mb-2">
						Techniker
					</div>
					{technicians.map((tech) => {
						const user = activeUsers.find(
							(u) => u.id === tech.profileId
						);
						if (!user) return null;

						return (
							<div
								key={tech.profileId}
								className="flex justify-between items-center text-xs"
							>
								<span className="font-medium">
									{generateLabel(user)}
								</span>
								<span className="text-gray-600 ml-2">
									{tech.percentage
										? `${tech.percentage}%`
										: "0%"}
								</span>
							</div>
						);
					})}
				</div>
			</PopoverContent>
		</Popover>
	);
};
